<template>
  <card card-body-classes="table-full-width">
    <h4 slot="header" class="card-title">{{ $t('operators.tableTitle') }}</h4>
    <div>
      <div
        class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
        <el-select
          class="select-primary mb-3 pagination-select"
          v-model="pagination.perPage"
          placeholder="Per page">
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"/>
        </el-select>

        <div class="row">
          <div class="col-6">
            <base-input>
              <el-input
                type="search"
                class="mb-3 search-input"
                prefix-icon="el-icon-search"
                :placeholder="$t('generic.searchInTablePlaceholder')"
                v-model="searchQuery"
                aria-controls="datatables"/>
            </base-input>
          </div>
          <div class="col-6" v-if="!fareGroupId && $auth.can({operator: 'create'}, 'permissions')">
            <create-operator-btn :fare-group-id="fareGroupId"/>
          </div>
        </div>
      </div>

      <el-table v-loading="fgLoading || loading" :data="queriedData" :empty-text="$t('generic.noData')">
        <el-table-column
          v-for="column in tableColumns"
          :key="column.label"
          :prop="column.prop"
          :label="$t(column.label)"
        >
        </el-table-column>
        <el-table-column v-if="!hideFareGroupColumn" :label="$t('operators.details.fare_group_id')">
          <div slot-scope="{ row }">
            <router-link v-if="row.fare_group_id" :to="fareGroupLink(row)">{{ row.fare_group_id }}</router-link>
          </div>
        </el-table-column>
        <!-- Updated & Created -->
        <el-table-column :label="$t('generic.updated')">
          <div slot-scope="{ row }">
            <!-- TODO: translate time -->
            {{ row.updated_at | time }}
          </div>
        </el-table-column>
        <el-table-column :label="$t('generic.created')">
          <div slot-scope="{ row }">
            <!-- TODO: translate time -->
            {{ row.created_at | time }}
          </div>
        </el-table-column>
        <el-table-column width="50">
          <div slot-scope="{ row }">
            <router-link :to="{name: 'operator', params: {id: row.id}}">
              <i class="tim-icons icon-double-right"></i>
            </router-link>
          </div>
        </el-table-column>
      </el-table>
    </div>
    <div
      slot="footer"
      class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
    >
      <div class="">
        <p class="card-category">
          {{ $t('generic.tablePaginationSummary', {from: from + 1, to: to, total: total}) }}
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      >
      </base-pagination>
    </div>
  </card>
</template>
<script>
import {BaseAlert, BasePagination} from "../../components";
import {Option, Select, Table, TableColumn} from "element-ui";
import Fuse from "fuse.js";
import CreateOperatorBtn from "./CreateOperatorBtn";
import {mapActions, mapState} from "vuex";
import linkUtils from "@/util/link_utils";

export default {
  name: 'operators-table',
  components: {
    BasePagination,
    BaseAlert,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    CreateOperatorBtn,
  },
  props: {
    editable: {
      type: Boolean,
      required: false,
      default: function () {
        return false;
      }
    },
    hideFareGroupColumn: {
      type: Boolean,
      required: false,
      default: function () {
        return false;
      }
    },
    fareGroupId: {
      type: String,
      required: false,
    },
  },
  computed: {
    ...mapState({
      fgLoading: state => state.fare_group_operators.loading,
      loading: state => state.operator.loading,
    }),
    /***
     * Returns a page from the searched data or the whole data. Search is performed in the watch section below
     */
    queriedData() {
      let result = this.tableData;
      if (this.searchedData.length > 0) {
        result = this.searchedData;
      }
      return result.slice(this.from, this.to);
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.searchedData.length > 0
        ? this.searchedData.length
        : this.tableData.length;
    }
  },
  data() {
    return {
      pagination: {
        perPage: 50,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50],
        total: 0
      },
      searchQuery: '',
      propsToSearch: ['id', 'name'],
      tableColumns: [
        {
          prop: 'id',
          label: 'generic.id',
        },
        {
          prop: 'name',
          label: 'operators.details.name',
        },
        {
          prop: 'type',
          label: 'generic.type',
        },
        {
          prop: 'status',
          label: 'generic.status',
        },
      ],
      tableData: [],
      searchedData: [],
      fuseSearch: null,
    };
  },
  methods: {
    ...mapActions(['LoadOperators', 'LoadFareGroupOperators']),
    fareGroupLink(row) {
      return linkUtils.fareGroupPath(this.$router, {fare_group_id: row.fare_group_id});
    }
  },
  mounted() {
    if (this.fareGroupId) {
      this.LoadFareGroupOperators(this.fareGroupId)
    } else {
      this.LoadOperators();
    }
  },
  watch: {
    "$store.state.fare_group_operators.data"(data) {
      this.tableData = data
      // Fuse search initialization.
      this.fuseSearch = new Fuse(this.tableData, {
        keys: this.propsToSearch,
        threshold: 0.3
      });
    },
    "$store.state.operator.data"(data) {
      this.tableData = data
      // Fuse search initialization.
      this.fuseSearch = new Fuse(this.tableData, {
        keys: this.propsToSearch,
        threshold: 0.3
      });
    },
    /**
     * Searches through the table data by a given query.
     * NOTE: If you have a lot of data, it's recommended to do the search on the Server Side and only display the results here.
     * @param value of the query
     */
    searchQuery(value) {
      let result = this.tableData;
      if (value !== '') {
        result = this.fuseSearch.search(this.searchQuery);
      }
      this.searchedData = result;
    },
  }
};
</script>
<style></style>
